import { useLayoutEffect, useRef, useState } from "react";
import { CHAT_FIT_REST_TOP_RATIO, type TChatFit, resolveChatFit } from "./chat-fit";

const REST_FIT: TChatFit = { scale: 1, top: null };

/* Measures the chat unit's natural height from a hidden, fully-rendered clone of
 the card (offsetHeight ignores the fit transform, so the reading stays honest)
 plus the live title block, and resolves the scale/top that keeps the whole unit
 inside the pinned stage on short viewports. */
export function useChatFit() {
	const cardMeasureRef = useRef<HTMLDivElement | null>(null);
	const titleMeasureRef = useRef<HTMLDivElement | null>(null);
	const [fit, setFit] = useState<TChatFit>(REST_FIT);
	const [cardHeight, setCardHeight] = useState(0);

	useLayoutEffect(() => {
		const update = () => {
			const stageHeight = window.innerHeight;
			const measuredCardHeight = cardMeasureRef.current?.offsetHeight ?? 0;
			const next = resolveChatFit({
				stageHeight,
				cardHeight: measuredCardHeight,
				titleHeight: titleMeasureRef.current?.offsetHeight ?? 0,
				restTop: stageHeight * CHAT_FIT_REST_TOP_RATIO,
			});
			setCardHeight((prev) => Math.max(prev, measuredCardHeight));
			setFit((prev) => (prev.scale === next.scale && prev.top === next.top ? prev : next));
		};

		update();
		const observer = new ResizeObserver(update);
		if (cardMeasureRef.current) observer.observe(cardMeasureRef.current);
		if (titleMeasureRef.current) observer.observe(titleMeasureRef.current);
		window.addEventListener("resize", update);
		return () => {
			observer.disconnect();
			window.removeEventListener("resize", update);
		};
	}, []);

	return {
		fitScale: fit.scale,
		fitTop: fit.top ?? undefined,
		cardHeight,
		cardMeasureRef,
		titleMeasureRef,
	};
}
